import { html, useState } from '/vendor/htm-preact.module.js';
import { useRead } from '/read.js';

/**
 * A league's month, as a month — GitHub issue #33.
 *
 * The Match Hub answers "what is on this week" as a list, which is the right
 * shape for reading results and the wrong one for "are we playing over the
 * bank holiday". A grid answers the second question by being a grid: the
 * empty squares are the information.
 *
 * Fixtures AND cup ties, because a knockout night is a night the league is
 * out whether or not it is a league fixture — `kind` colours them apart
 * rather than filtering one out.
 *
 * One request per month, over the month actually on screen. A season's worth
 * of fixtures fetched up front would be most of a megabyte to draw thirty
 * squares from.
 */
export function LeagueCalendar({ leagueId, onGo }) {
  // Anchored on a real date rather than an offset counter, so "today" stays
  // meaningful when the month moves: an offset makes the current month a
  // number to keep in step with the clock.
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  // Formatted from LOCAL parts, never `toISOString()`. A Date built as local
  // midnight converts to 23:00 the previous day under BST, so every square
  // would be labelled with -- and would look up -- the wrong date, half the
  // year. A calendar is about the day somebody is standing in.
  const iso = (d) => `${d.getFullYear()}-`
    + `${String(d.getMonth() + 1).padStart(2, '0')}-`
    + `${String(d.getDate()).padStart(2, '0')}`;

  const [fixtures, error] = useRead(
    `/api/leagues/${encodeURIComponent(leagueId)}/fixtures`
    + `?start=${iso(first)}&end=${iso(next)}`);

  // Monday-first, which is how a pool league's week runs. `getDay()` is
  // Sunday-first, so Sunday becomes 6 rather than 0.
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

  const byDay = {};
  for (const f of fixtures || []) {
    (byDay[f.played_on] = byDay[f.played_on] || []).push(f);
  }

  const today = new Date();
  const isToday = (d) => (d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth() && d.getDate() === today.getDate());

  const step = (by) => setMonth(
    new Date(month.getFullYear(), month.getMonth() + by, 1));
  const title = month.toLocaleDateString(undefined,
                                         { month: 'long', year: 'numeric' });

  const cells = [];
  for (let i = 0; i < lead; i += 1) {
    cells.push(html`<div class="cal-cell cal-blank" key=${`b${i}`}></div>`);
  }
  for (let d = 1; d <= days; d += 1) {
    const date = new Date(month.getFullYear(), month.getMonth(), d);
    const on = byDay[iso(date)] || [];
    cells.push(html`
      <div class=${`cal-cell${isToday(date) ? ' cal-today' : ''}`}
           key=${`d${d}`} data-testid=${`cal-day-${iso(date)}`}>
        <span class="cal-num">${d}</span>
        ${/* A COUNT per kind, not a list of names (issue #33). Twelve
              fixtures in a square this size is twelve unreadable lines, and
              the question a month view answers is "how busy is that night" --
              "LM 12" answers it at a glance where twelve clipped pub names
              answer nothing. The day opens for the detail. */''}
        ${['league', 'cup'].map((kind) => {
          const got = on.filter((f) => (f.kind || 'league') === kind);
          if (!got.length) return null;
          return html`
            <button key=${kind} class="cal-fix"
                    data-testid=${`cal-${kind}-${iso(date)}`}
                    title=${`${got.length} `
                      + `${kind === 'cup' ? 'competition' : 'league'} `
                      + `${got.length === 1 ? 'match' : 'matches'}`}
                    onClick=${() => onGo('league', leagueId, 'fixtures')}>
              <span class=${kind === 'cup' ? 'cal-cup' : ''}>
                ${`${kind === 'cup' ? 'CM' : 'LM'} ${got.length}`}
              </span>
            </button>`;
        })}
      </div>`);
  }

  return html`
    <div class="stack" data-testid="league-calendar">
      <div class="rowbetween">
        <button class="secondary" style="width:auto"
                data-testid="cal-prev" onClick=${() => step(-1)}>←</button>
        <span class="mono" data-testid="cal-month">${title}</span>
        <button class="secondary" style="width:auto"
                data-testid="cal-next" onClick=${() => step(1)}>→</button>
      </div>

      ${error && html`<p class="error">${error.detail || error.message}</p>`}

      <div class="cal-week">
        ${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => html`
          <span class="sub" key=${d}>${d}</span>`)}
      </div>
      <div class="cal-grid">${cells}</div>

      ${/* Absent, not "0 fixtures": an empty month in a grid already says
            so, and a line underneath saying it again reads as an error. */''}
      ${fixtures !== undefined && fixtures.length === 0 && html`
        <p class="empty" data-testid="cal-empty">
          Nothing on in ${title}.
        </p>`}
    </div>`;
}
